import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import {
    SUPERSCRIPT,
    OrderOfOperationsLevelOptions,
    ProblemData,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Order-of-operations problem generation. See ADR 0019.
 *
 * An expression is a flat sequence of operands and operators, plus an optional
 * parenthesised span and an optional squared/cubed term. It is generated, then
 * checked two ways and rejected unless:
 *
 *  - correct evaluation gives a different answer from left-to-right (§2), so
 *    every problem punishes the misconception the sheet exists to find;
 *  - every intermediate result of the correct evaluation is a whole number,
 *    and every division divides evenly (§3).
 *
 * Non-negativity is the **default** rather than the rule (ADR 0036 §2):
 * `AllowNegatives` relaxes it, and is off unless deliberately turned on,
 * because a fourth grader doing `4 + 3 × 2` has not met negative numbers.
 * Exact division does not relax with it - that half of §3 has nothing to do
 * with sign.
 *
 * Rejected draws are redrawn, not repaired, with a `total * 40` attempt limit.
 */

const OPS = ["+", "-", "×", "÷"] as const;
type Op = (typeof OPS)[number];

// Division forces an even-division check that rejects most draws, so it is
// drawn less often than the other three. The mix still teaches ÷ precedence.
const OP_POOL: Op[] = ["+", "+", "-", "-", "×", "×", "×", "÷"];

interface Expression {
    operands: number[];
    ops: Op[]; // length operands.length - 1
    /** Inclusive operand-index span wrapped in parentheses, or null. */
    paren: [number, number] | null;
    /** Operand index raised to a power, or null. */
    exponent: { index: number; power: 2 | 3 } | null;
}

function randomOperand(): number {
    return getRandomInt(2, 12);
}

function randomOp(): Op {
    return OP_POOL[getRandomInt(0, OP_POOL.length - 1)];
}

function operandCountFor(level: OrderOfOperationsLevelOptions): number {
    switch (level) {
        case OrderOfOperationsLevelOptions.TwoOperations:
            return 3;
        case OrderOfOperationsLevelOptions.ThreeOperations:
        case OrderOfOperationsLevelOptions.Parentheses:
            return 4;
        case OrderOfOperationsLevelOptions.Exponents:
            return getRandomInt(0, 1) === 0 ? 3 : 4;
        default:
            return 3;
    }
}

function drawExpression(level: OrderOfOperationsLevelOptions): Expression {
    const count = operandCountFor(level);
    const operands = Array.from({ length: count }, randomOperand);
    const ops: Op[] = Array.from({ length: count - 1 }, randomOp);

    let paren: [number, number] | null = null;
    if (level === OrderOfOperationsLevelOptions.Parentheses) {
        const start = getRandomInt(0, count - 2);
        let end = getRandomInt(start + 1, count - 1);
        // Parentheses around the whole expression change nothing; keep at least
        // one operand outside them.
        if (start === 0 && end === count - 1) {
            end = count - 2;
        }
        paren = [start, end];
    }

    let exponent: Expression["exponent"] = null;
    if (level === OrderOfOperationsLevelOptions.Exponents) {
        exponent = {
            index: getRandomInt(0, count - 1),
            power: getRandomInt(0, 1) === 0 ? 2 : 3,
        };
        // Keep a squared/cubed base small so the value stays reasonable.
        operands[exponent.index] = getRandomInt(2, exponent.power === 2 ? 9 : 5);
    }

    return { operands, ops, paren, exponent };
}

/** The operand values with any exponent already applied. */
function resolvedOperands(expr: Expression): number[] {
    const values = [...expr.operands];
    if (expr.exponent) {
        values[expr.exponent.index] = values[expr.exponent.index] ** expr.exponent.power;
    }
    return values;
}

const apply: Record<Op, (a: number, b: number) => number> = {
    "+": (a, b) => a + b,
    "-": (a, b) => a - b,
    "×": (a, b) => a * b,
    "÷": (a, b) => a / b,
};

/**
 * Evaluates a flat operand/operator sequence with correct precedence
 * (multiplication and division first, left to right; then addition and
 * subtraction). Returns null if any step is fractional, or negative unless
 * the sheet allows negatives.
 */
function evaluatePrecedence(
    operands: number[],
    ops: Op[],
    allowNegatives = false
): number | null {
    let values = [...operands];
    let operators = [...ops];

    for (const pass of [["×", "÷"], ["+", "-"]] as Op[][]) {
        let i = 0;
        while (i < operators.length) {
            if (pass.includes(operators[i])) {
                const result = apply[operators[i]](values[i], values[i + 1]);
                // Fractional is always a rejection; negative only when the
                // sheet has not asked for negatives (ADR 0036 §2).
                if (!Number.isInteger(result) || (result < 0 && !allowNegatives)) {
                    return null;
                }
                values.splice(i, 2, result);
                operators.splice(i, 1);
            } else {
                i++;
            }
        }
    }
    return values[0];
}

/** Correct evaluation of the whole expression, parentheses included, or null. */
function evaluateCorrect(expr: Expression, allowNegatives = false): number | null {
    const values = resolvedOperands(expr);
    const ops = [...expr.ops];

    if (expr.paren) {
        const [start, end] = expr.paren;
        const inner = evaluatePrecedence(
            values.slice(start, end + 1),
            ops.slice(start, end),
            allowNegatives
        );
        if (inner === null) {
            return null;
        }
        values.splice(start, end - start + 1, inner);
        ops.splice(start, end - start);
    }

    return evaluatePrecedence(values, ops, allowNegatives);
}

/** Strict left-to-right evaluation, ignoring precedence and parentheses. */
function evaluateLeftToRight(expr: Expression): number {
    const values = resolvedOperands(expr);
    let acc = values[0];
    for (let i = 0; i < expr.ops.length; i++) {
        acc = apply[expr.ops[i]](acc, values[i + 1]);
    }
    return acc;
}

// Imported rather than redeclared: the two-and-three limit is a font
// constraint shared with NumberPropertyGenerator, and ADR 0037 §2 says why.
// SUPERSCRIPT now lives in Constants.ts.

function writeExpression(expr: Expression): string {
    const parts: string[] = [];
    for (let i = 0; i < expr.operands.length; i++) {
        if (expr.paren && expr.paren[0] === i) {
            parts.push("(");
        }
        let operand = String(expr.operands[i]);
        if (expr.exponent && expr.exponent.index === i) {
            operand += SUPERSCRIPT[expr.exponent.power];
        }
        parts.push(operand);
        if (expr.paren && expr.paren[1] === i) {
            parts.push(")");
        }
        if (i < expr.ops.length) {
            parts.push(expr.ops[i]);
        }
    }
    // Join with spaces, but keep "(" and ")" tight against their operand.
    return parts
        .join(" ")
        .replace(/\(\s+/g, "(")
        .replace(/\s+\)/g, ")");
}

/** One expression problem, or null when the draw fails a rule. */
export function buildOrderOfOperationsProblem(
    level: OrderOfOperationsLevelOptions,
    key: number,
    allowNegatives = false
): ProblemData | null {
    const expr = drawExpression(level);
    const correct = evaluateCorrect(expr, allowNegatives);
    if (correct === null) {
        return null;
    }

    const leftToRight = evaluateLeftToRight(expr);
    // Every problem must punish the wrong order (§2).
    if (correct === leftToRight) {
        return null;
    }

    const expression = writeExpression(expr);
    return {
        key,
        numbers: [...expr.operands, correct],
        operator: "",
        display: [expression, String(correct)],
        isExpression: true,
    };
}

/** A page-shaped set of expression problems, matching GenerateDataV2's shape. */
export function generateOrderOfOperationsPages(
    settings: ISettingsProps
): ProblemData[][] | null {
    const level =
        settings.OrderOfOperationsLevel ?? OrderOfOperationsLevelOptions.TwoOperations;
    const allowNegatives = settings.AllowNegatives === true;
    // Nothing here varies by question type - one level, no rotation - so the
    // helper's fair sequence is over a single-element list (ADR 0042 §2).
    return generateProblemPages({
        settings,
        choices: [level],
        build: (chosenLevel, key) =>
            buildOrderOfOperationsProblem(chosenLevel, key, allowNegatives),
        fingerprint: (problem) => problem.display![0],
    });
}
